/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { IconProps } from '@coursera/cds-common';
import { ButtonGroup, Dialog, Typography2 } from '@coursera/cds-core';
import { HonorsAssignmentIcon, ReadingIcon } from '@coursera/cds-icons';

import ActionThumbnailBackground from 'bundles/enterprise-admin-learners/components/invite-learners/ActionThumbnailBackground';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  recommendedActionsList: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    list-style: none;
    padding: 0;
    margin: 0;
  `,
  recommendedAction: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
  `,
  recommendedActionThumbnail: css`
    width: 58px;
    height: 58px;
    flex-shrink: 0;
    border-radius: var(--cds-border-radius-100);
    background: var(--cds-color-emphasis-primary-background-xweak);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  `,
  recommendedActionIcon: css`
    position: absolute;
  `,
  recommendedActionDescription: css`
    margin-top: var(--cds-spacing-50);
  `,
};

type RecommendedAction = { icon: React.ComponentType<IconProps>; title: string; description: string };

const getRecommendedActions = (): RecommendedAction[] => [
  {
    icon: HonorsAssignmentIcon,
    title: _t('Assign content to learners'),
    description: _t('Choose specific courses or learning paths for your learners to complete.'),
  },
  {
    icon: ReadingIcon,
    title: _t('Curate a catalog for your learners'),
    description: _t("Build collections and learning paths tailored to each group's learning goals."),
  },
];

export type InviteSuccessDialogProps = {
  open: boolean;
  onClose: () => void;
  invitedCount: number;
  groupName: string;
  canViewGroup: boolean;
  onViewGroup: () => void;
};

/** Shown when every invite succeeded — offers next-step suggestions and a link to the group. */
const InviteSuccessDialog = ({
  open,
  onClose,
  invitedCount,
  groupName,
  canViewGroup,
  onViewGroup,
}: InviteSuccessDialogProps) => {
  const heading =
    invitedCount === 1
      ? _t('1 learner successfully invited and added to the #{groupName} group', { groupName })
      : _t('#{invitedCount} learners successfully invited and added to the #{groupName} group', {
          invitedCount,
          groupName,
        });

  return (
    <Dialog open={open} onClose={onClose} width="medium">
      <HeadingGroup>{heading}</HeadingGroup>
      <Content css={styles.content}>
        <Typography2 component="p" color="supportText">
          {_t('Here are a few things you can do next to set your learners up for success.')}
        </Typography2>
        <ul css={styles.recommendedActionsList}>
          {getRecommendedActions().map(({ icon: Icon, title, description }) => (
            <li key={title} css={styles.recommendedAction}>
              <div css={styles.recommendedActionThumbnail}>
                <ActionThumbnailBackground />
                <Icon size="large" color="interactive" css={styles.recommendedActionIcon} aria-hidden />
              </div>
              <div>
                <Typography2 component="h3" variant="subtitleSmall">
                  {title}
                </Typography2>
                <Typography2
                  component="p"
                  variant="bodyTertiary"
                  color="supportText"
                  css={styles.recommendedActionDescription}
                >
                  {description}
                </Typography2>
              </div>
            </li>
          ))}
        </ul>
      </Content>
      <Actions>
        <ButtonGroup
          primaryButtonProps={{ onClick: onViewGroup, disabled: !canViewGroup, children: _t('View group') }}
          alternateButtonProps={{ variant: 'secondary', onClick: onClose, children: _t('Done') }}
        />
      </Actions>
    </Dialog>
  );
};

export default InviteSuccessDialog;
